import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  Header,
  Image,
  Modal,
  Segment,
  Grid,
  Icon,
  Table,
} from "semantic-ui-react";
import "../../node_modules/react-vis/dist/style.css";
import "../css/home.css";
import CompanyPage from "../company/CompanyPage";
import { useMobile } from "../MobileContext";
import Constants from "../util/Constants";

const PerformanceView = ({ stockReports }) => {
  const isMobile = useMobile().isMobile;
  const [modalState, setModalState] = useState({
    open: false,
    modalSymbol: "",
  });
  const handleShowModal = (symbol) => {
    setModalState({ ...modalState, modalSymbol: symbol, open: true });
  };

  const handleCloseModal = () => {
    setModalState({ ...modalState, open: false });
  };

  // Group stocks by performance only (no sector/industry grouping)
  const performanceData = useMemo(() => {
    if (!stockReports || stockReports.length === 0) return { struggling: [], moderate: [], outperforming: [] };

    const struggling = [];
    const moderate = [];
    const outperforming = [];

    stockReports.forEach((stock) => {
      const perfYear = Number(stock.perfYear) || 0;
      if (perfYear < 0) {
        struggling.push(stock);
      } else if (perfYear >= 0 && perfYear < 10) {
        moderate.push(stock);
      } else {
        outperforming.push(stock);
      }
    });

    // Sort each group by performance
    const sortByPerformance = (a, b) => {
      const perfA = Number(a.perfYear) || 0;
      const perfB = Number(b.perfYear) || 0;
      return perfB - perfA; // Descending: best (highest) to worst (lowest)
    };

    struggling.sort(sortByPerformance);
    moderate.sort(sortByPerformance);
    outperforming.sort(sortByPerformance);

    return { struggling, moderate, outperforming };
  }, [stockReports]);

  if (!stockReports || stockReports.length === 0) {
    return (
      <div
        style={{
          padding: "2rem",
          textAlign: "center",
          color: "#666",
        }}
      >
        No stock data available
      </div>
    );
  }

  // Render stock logos component (reusable)
  const renderStockLogos = (stocks, color) => {
    const gap = isMobile ? "0.25rem" : "0.4rem";
    // Calculate width for 7 items per row: (100% - 6 gaps) / 7
    // 6 gaps: mobile = 1.5rem, desktop = 2.4rem
    const totalGapWidth = isMobile ? "1.5rem" : "2.4rem";
    const itemWidth = `calc((100% - ${totalGapWidth}) / 7)`;
    
    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: gap,
          alignItems: "flex-start",
          justifyContent: "flex-start",
          width: "100%",
        }}
      >
        {stocks.map((stock) => (
          <div
            key={stock.symbol}
            style={{
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "4px",
              width: itemWidth,
              minWidth: itemWidth,
              maxWidth: itemWidth,
              flexShrink: 0,
              flexBasis: itemWidth,
            }}
          >
          <div
            onClick={() => handleShowModal(stock.symbol)}
            style={{
              cursor: "pointer",
              transition: "all 0.2s ease",
              padding: "4px",
              background: "linear-gradient(145deg, #6b7280, #4b5563)",
              borderRadius: "6px",
              border: "1px solid rgba(255,255,255,0.1)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 6px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.1)",
              width: isMobile ? "40px" : "48px",
              height: isMobile ? "40px" : "48px",
              position: "relative",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px) scale(1.05)";
              e.currentTarget.style.boxShadow = `0 4px 12px ${color}50, inset 0 1px 0 rgba(255,255,255,0.15)`;
              e.currentTarget.style.borderColor = color;
              e.currentTarget.style.background = "linear-gradient(145deg, #7b8280, #5b6563)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.1)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
              e.currentTarget.style.background = "linear-gradient(145deg, #6b7280, #4b5563)";
            }}
          >
            {stock.image ? (
              <Image
                src={stock.image}
                size="mini"
                rounded
                style={{
                  margin: "0",
                  width: isMobile ? "32px" : "40px",
                  height: isMobile ? "32px" : "40px",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                  const fallback = e.target.parentElement.querySelector(".logo-fallback");
                  if (fallback) fallback.style.display = "flex";
                }}
              />
            ) : null}
            <span
              className="logo-fallback"
              style={{
                display: (stock.image || stock.symbol) ? "none" : "flex",
                alignItems: "center",
                justifyContent: "center",
                width: isMobile ? "32px" : "40px",
                height: isMobile ? "32px" : "40px",
                color: "white",
                fontSize: isMobile ? "0.45rem" : "0.55rem",
                fontWeight: "600",
                textAlign: "center",
              }}
            >
              {stock.symbol}
            </span>
          </div>
          <span
            style={{
              fontSize: isMobile ? "0.6rem" : "0.7rem",
              color: "#333",
              fontWeight: "600",
              textAlign: "center",
              lineHeight: 1.1,
              letterSpacing: "0.1px",
            }}
          >
            {stock.symbol}
          </span>
          <span
            style={{
              fontSize: isMobile ? "0.55rem" : "0.65rem",
              color: (() => {
                const perfYear = Number(stock.perfYear) || 0;
                return perfYear >= 0 ? "#21ba45" : "#db2828";
              })(),
              fontWeight: "600",
              textAlign: "center",
              lineHeight: 1.1,
            }}
          >
            {(() => {
              const perfYear = Number(stock.perfYear);
              if (isNaN(perfYear)) return "N/A";
              return `${perfYear >= 0 ? "+" : ""}${perfYear.toFixed(1)}%`;
            })()}
          </span>
        </div>
      ))}
      </div>
    );
  };

  // Render performance group card
  const renderPerformanceCard = (stocks, color, title, count) => {
    const cardPadding = isMobile ? "0.625rem" : "0.75rem";
    
    return (
    <Card
      style={{
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)",
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        opacity: stocks.length > 0 ? 1 : 0.6,
        height: "100%",
        margin: 0,
        width: "100%",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <Card.Content style={{ padding: cardPadding, width: "100%", boxSizing: "border-box" }}>
        {stocks.length > 0 ? (
          renderStockLogos(stocks, color)
        ) : (
          <span
            style={{
              color: "#999",
              fontSize: isMobile ? "0.6rem" : "0.65rem",
              fontStyle: "italic",
            }}
          >
            No stocks
          </span>
        )}
      </Card.Content>
    </Card>
    );
  };

  return (
    <div
      style={{
        minHeight: "74vh",
        padding: isMobile ? "0.75rem" : "1rem",
        marginBottom: "56px",
        background: "linear-gradient(135deg, #f5f7fa 0%, #e8ecf0 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        maxWidth: isMobile ? "100vw" : "none",
        overflowX: "hidden",
      }}
    >
      <Header
        as="h5"
        textAlign="left"
        style={{
          marginBottom: "1rem",
          color: "#6b7280",
          fontWeight: "500",
          fontSize: isMobile ? "0.85rem" : "0.9rem",
          width: "100%",
          maxWidth: isMobile ? "98%" : "95%",
          paddingLeft: "0.5rem",
        }}
      >
        Performance based on 1-Year returns
      </Header>

      <Grid 
        stackable 
        columns={3} 
        style={{ 
          width: "100%", 
          maxWidth: isMobile ? "100%" : "1600px", 
          margin: "0 auto"
        }}
      >
        {/* Underperforming Card */}
        <Grid.Column style={{ paddingLeft: isMobile ? "0.5rem" : "0.25rem", paddingRight: isMobile ? "0.5rem" : "0.25rem", display: "flex", justifyContent: "center", alignItems: "stretch" }}>
          <Card
            style={{
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)",
              background: "#ffffff",
              border: "1px solid #e5e7eb",
              margin: 0,
              width: "100%",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              height: "100%",
            }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = "0 6px 12px rgba(211,47,47,0.2), 0 4px 6px rgba(0,0,0,0.1)";
            e.currentTarget.style.transform = "translateY(-2px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <Card.Content style={{ padding: isMobile ? "0.75rem" : "1rem" }}>
            <Header
              as="h5"
              style={{
                margin: "0 0 1rem 0",
                color: "#991b1b",
                fontWeight: "700",
                fontSize: isMobile ? "0.9rem" : "1rem",
                background: "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
                padding: "0.5rem 0.75rem",
                borderRadius: "6px",
              }}
            >
              Underperforming (&lt; 0%)
              {performanceData.struggling.length > 0 && (
                <span style={{ fontSize: "0.75rem", fontWeight: "500", opacity: 0.8, marginLeft: "0.5rem" }}>
                  ({performanceData.struggling.length})
                </span>
              )}
            </Header>
            {performanceData.struggling.length > 0 ? (
              renderStockLogos(performanceData.struggling, "#d32f2f")
            ) : (
              <span
                style={{
                  color: "#999",
                  fontSize: isMobile ? "0.6rem" : "0.65rem",
                  fontStyle: "italic",
                  display: "block",
                  textAlign: "center",
                  padding: "1rem",
                }}
              >
                No stocks
              </span>
            )}
          </Card.Content>
          </Card>
        </Grid.Column>

        {/* Performing Steadily Card */}
        <Grid.Column style={{ paddingLeft: isMobile ? "0.5rem" : "0.25rem", paddingRight: isMobile ? "0.5rem" : "0.25rem", display: "flex", justifyContent: "center", alignItems: "stretch" }}>
          <Card
            style={{
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)",
              background: "#ffffff",
              border: "1px solid #e5e7eb",
              margin: 0,
              width: "100%",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              height: "100%",
            }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = "0 6px 12px rgba(237,108,2,0.2), 0 4px 6px rgba(0,0,0,0.1)";
            e.currentTarget.style.transform = "translateY(-2px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <Card.Content style={{ padding: isMobile ? "0.75rem" : "1rem" }}>
            <Header
              as="h5"
              style={{
                margin: "0 0 1rem 0",
                color: "#9a3412",
                fontWeight: "700",
                fontSize: isMobile ? "0.9rem" : "1rem",
                background: "linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)",
                padding: "0.5rem 0.75rem",
                borderRadius: "6px",
              }}
            >
              Performing Steadily (0-10%)
              {performanceData.moderate.length > 0 && (
                <span style={{ fontSize: "0.75rem", fontWeight: "500", opacity: 0.8, marginLeft: "0.5rem" }}>
                  ({performanceData.moderate.length})
                </span>
              )}
            </Header>
            {performanceData.moderate.length > 0 ? (
              renderStockLogos(performanceData.moderate, "#ed6c02")
            ) : (
              <span
                style={{
                  color: "#999",
                  fontSize: isMobile ? "0.6rem" : "0.65rem",
                  fontStyle: "italic",
                  display: "block",
                  textAlign: "center",
                  padding: "1rem",
                }}
              >
                No stocks
              </span>
            )}
          </Card.Content>
          </Card>
        </Grid.Column>

        {/* Outperforming Card */}
        <Grid.Column style={{ paddingLeft: isMobile ? "0.5rem" : "0.25rem", paddingRight: isMobile ? "0.5rem" : "0.25rem", display: "flex", justifyContent: "center", alignItems: "stretch" }}>
          <Card
            style={{
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)",
              background: "#ffffff",
              border: "1px solid #e5e7eb",
              margin: 0,
              width: "100%",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              height: "100%",
            }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = "0 6px 12px rgba(46,125,50,0.2), 0 4px 6px rgba(0,0,0,0.1)";
            e.currentTarget.style.transform = "translateY(-2px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <Card.Content style={{ padding: isMobile ? "0.75rem" : "1rem" }}>
            <Header
              as="h5"
              style={{
                margin: "0 0 1rem 0",
                color: "#166534",
                fontWeight: "700",
                fontSize: isMobile ? "0.9rem" : "1rem",
                background: "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
                padding: "0.5rem 0.75rem",
                borderRadius: "6px",
              }}
            >
              Outperforming (&gt; 10%)
              {performanceData.outperforming.length > 0 && (
                <span style={{ fontSize: "0.75rem", fontWeight: "500", opacity: 0.8, marginLeft: "0.5rem" }}>
                  ({performanceData.outperforming.length})
                </span>
              )}
            </Header>
            {performanceData.outperforming.length > 0 ? (
              renderStockLogos(performanceData.outperforming, "#2e7d32")
            ) : (
              <span
                style={{
                  color: "#999",
                  fontSize: isMobile ? "0.6rem" : "0.65rem",
                  fontStyle: "italic",
                  display: "block",
                  textAlign: "center",
                  padding: "1rem",
                }}
              >
                No stocks
              </span>
            )}
          </Card.Content>
          </Card>
        </Grid.Column>
      </Grid>

      <Modal
        closeOnEscape
        centered
        open={modalState.open}
        onClose={handleCloseModal}
        size="fullscreen"
        style={{
          marginLeft: "2%",
          backgroundColor: "rgba(0,0,0,0.85)",
        }}
      >
        <Modal.Content scrolling style={{ padding: "1rem" }}>
          <Modal.Description>
            <CompanyPage symbol={modalState.modalSymbol} />
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions style={{ padding: "1rem" }}>
          <Button primary onClick={handleCloseModal} style={buttonStyle}>
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

const buttonStyle = {
  backgroundColor: "#3498db",
  color: "white",
  padding: "0.6rem 1.5rem",
  borderRadius: "6px",
  border: "none",
  fontWeight: "500",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#2980b9",
    transform: "scale(1.02)",
  },
};

export default PerformanceView;
